import { FormattedDate, FormattedMessage } from "react-intl";

import styled from "styled-components";

import { MailOutlined } from "@ant-design/icons";

import { Typography } from "antd";

import { CmtActiveField } from "@app/components/users/field/CmtActiveField.tsx";
import { CmtAvatarField } from "@app/components/users/field/CmtAvatarField.tsx";
import { CmtBenchField } from "@app/components/users/field/CmtBenchField.tsx";
import { CmtDepartmentField } from "@app/components/users/field/CmtDepartmentField.tsx";
import { User } from "@app/models/User.ts";
import { useSeniorityFormat } from "@app/services/hook/useSeniorityFormat.tsx";
import { capitalizeFirstLetter } from "@app/services/utils/userUtils.ts";
import { colors, fonts } from "@app/theme.ts";

const StyledHero = styled.section`
  overflow: hidden;
  margin-bottom: 1.5rem;
  border: 1px solid ${colors.border};
  border-radius: 20px;
  background: ${colors.white};
`;

const StyledBanner = styled.div`
  height: 120px;
  background: radial-gradient(
      circle at 85% 20%,
      ${colors.pink}55 0,
      transparent 45%
    ),
    linear-gradient(120deg, ${colors.navy} 0%, ${colors["navy-3"]} 100%);
`;

const StyledBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1.5rem;
  padding: 1rem 2rem 1.75rem;
`;

const StyledAvatar = styled.div`
  margin-top: -80px;
`;

const StyledIdentity = styled.div`
  flex: 1;
  min-width: 260px;
`;

const StyledName = styled.h1`
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const StyledJob = styled.div`
  color: ${colors.pink};
  font-family: ${fonts.hand};
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
`;

const StyledMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  margin-top: 0.75rem;

  .ant-tag {
    margin-inline-end: 0;
  }
`;

const StyledStats = styled.div`
  display: flex;
  gap: 2rem;
`;

const StyledStat = styled.div`
  display: flex;
  flex-direction: column;

  span:first-child {
    color: ${colors.grey2};
    font-size: 0.8rem;
  }

  span:last-child {
    font-size: 1.1rem;
    font-weight: 700;
  }
`;

interface CmtUserHeroProps {
  user: User;
}

export const CmtUserHero = ({ user }: CmtUserHeroProps) => {
  const seniority = useSeniorityFormat(user.startingDate);

  return (
    <StyledHero>
      <StyledBanner />
      <StyledBody>
        <StyledAvatar>
          <CmtAvatarField user={user} size={128} />
        </StyledAvatar>
        <StyledIdentity>
          {user.userJob?.name && <StyledJob>{user.userJob.name}</StyledJob>}
          <StyledName>
            {capitalizeFirstLetter(user.firstName)}{" "}
            {user.lastName.toUpperCase()}
          </StyledName>
          <StyledMeta>
            <CmtActiveField isActive={user.isActive} />
            {user.isOnBench && <CmtBenchField />}
            <CmtDepartmentField department={user.department} />
            <Typography.Text copyable={{ text: user.email }}>
              <Typography.Link href={`mailto:${user.email}`}>
                <MailOutlined /> {user.email}
              </Typography.Link>
            </Typography.Text>
          </StyledMeta>
        </StyledIdentity>
        <StyledStats>
          <StyledStat>
            <span>
              <FormattedMessage
                id="table.starting_date"
                defaultMessage="Starting Date"
              />
            </span>
            <span>
              <FormattedDate
                value={new Date(user.startingDate)}
                dateStyle="medium"
              />
            </span>
          </StyledStat>
          <StyledStat>
            <span>
              <FormattedMessage
                id="collaborator.seniority"
                defaultMessage="Seniority"
              />
            </span>
            <span>{seniority}</span>
          </StyledStat>
        </StyledStats>
      </StyledBody>
    </StyledHero>
  );
};
